import { updateComplianceConsents } from '@/api/account';
import { getStoreInstance } from '@/store';
import logger from '@/utils/client-logger';

export interface ComplianceConsentExtra {
  email?: string;
  tel_code?: string;
  telephone?: string;
}

export interface UpdateConsentsOptions {
  /** 场景码，与 global init 返回的 compliance_policies 的 key 一致，如 web.login、web.register */
  sceneCode: string;
  extra?: ComplianceConsentExtra;
  /** 页面中文名（埋点/合规页标识），如 route 元信息中的 cnName */
  page?: string;
  status?: number;
  consentTypes?: number[];
}

type CompliancePolicyRow = {
  consent_type: number;
  id?: string | number;
  policy_id?: string | number;
  policy_version: string | number;
};

type ComplianceScene = {
  policies?: CompliancePolicyRow[];
};

/**
 * 登录/注册成功后：根据 global init 下发的 compliance_policies 自动上报协议同意；
 * 上报成功后会调用 store 的 getMarketingStatus 刷新 complianceMarketingStatus。
 * 可在任意客户端上下文调用（内部读 store）；失败仅打日志，不抛错、不打扰用户。
 */
export async function updateConsentsAfterAuth(options: UpdateConsentsOptions) {
  try {
    const { sceneCode, extra, page, consentTypes, status } = options;
    const store = getStoreInstance();
    const compliancePolicies = store?.getState().compliancePolicies;
    const scene = compliancePolicies?.[sceneCode] as ComplianceScene | undefined;
    const policies = scene?.policies || [];
    if (!policies.length) return;

    const targetPolicies =
      consentTypes && consentTypes.length
        ? policies.filter((policy) => consentTypes.includes(policy.consent_type))
        : policies;
    if (!targetPolicies.length) return;

    const items = targetPolicies.map((policy) => {
      const policyId = policy.id ?? policy.policy_id;
      const item: {
        consent_type: number;
        policy_id: string | number;
        policy_version: string | number;
        email?: string;
        tel_code?: string;
        telephone?: string;
        status?: number;
      } = {
        consent_type: policy.consent_type,
        policy_id: policyId as string | number,
        policy_version: policy.policy_version,
      };
      if (status !== undefined) {
        item.status = status;
      }
      if (policy.consent_type === 3 && extra?.email) {
        item.email = extra.email;
      }
      if ((policy.consent_type === 4 || policy.consent_type === 5) && extra?.tel_code) {
        item.tel_code = extra.tel_code;
      }
      if ((policy.consent_type === 4 || policy.consent_type === 5) && extra?.telephone) {
        item.telephone = extra.telephone;
      }
      return item;
    });

    await updateComplianceConsents({
      scene_code: sceneCode,
      page: page ?? '',
      items,
    });

    await store?.getState().getMarketingStatus();
  } catch (error) {
    logger.error(error, `useComplianceConsent: updateConsentsAfterAuth ${options.sceneCode}`);
  }
}
export function useComplianceConsent() {
  return { updateConsentsAfterAuth };
}
